import { useMemo, useState, type SyntheticEvent } from "react"
import { ResetPasswordInputs } from "@/types/auth"

import { errorToAmplitudeProperties } from "@/lib/analytics/amplitude-properties"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { getResetPasswordErrorMessage } from "@/lib/auth/errors"
import { buildClientAxios } from "@/lib/client-axios"
import { validateSetPasswordFormValue } from "@/lib/set-password-form"
import {
  selectAuthFlowCallbackUrl,
  selectAuthFlowError,
  selectAuthFlowResetPasswordToken,
  setAuthFlowError,
  setAuthFlowLoading,
} from "@/lib/storage/auth-slice"
import { credentialSignIn } from "@/lib/storage/auth-thunks"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { shouldTurnstile, turnstileErrorAction } from "@/lib/turnstile"
import { UserApi } from "@/lib/user-api"
import { Turnstile } from "@/components/turnstile"
import { Button } from "../ui/button"
import { Form, FormControl, FormError, FormField } from "../ui/form"
import { TextInput } from "../ui/text-input"

export default function ResetPassword() {
  const dispatch = useAppDispatch()
  const resetPasswordToken = useAppSelector(selectAuthFlowResetPasswordToken)
  const callbackUrl = useAppSelector(selectAuthFlowCallbackUrl)
  const error = useAppSelector(selectAuthFlowError)
  const userApi = useMemo(() => new UserApi(buildClientAxios()), [])

  const [submitting, setSubmitting] = useState<boolean>(false)
  const [resetPasswordInputs, setResetPasswordInputs] =
    useState<ResetPasswordInputs>({
      password: "",
      confirmPassword: "",
    })
  const [formErrors, setFormErrors] = useState<{
    password: string[]
    confirmPassword: string[]
  }>({
    password: [],
    confirmPassword: [],
  })

  const [cfToken, setCfToken] = useState("")

  const handleTurnstileRetry = () => {
    setCfToken("")
  }

  const handleTurnstileError = (errorCode: string) => {
    if (turnstileErrorAction(errorCode) !== "terminal") {
      handleTurnstileRetry()
      return
    }

    handleTurnstileFailure()
  }

  const handleTurnstileFailure = () => {
    setCfToken("")
    dispatch(setAuthFlowError("Please complete the verification"))
  }

  if (!resetPasswordToken) {
    return null
  }

  const validateForm = ({ password, confirmPassword }: ResetPasswordInputs) => {
    const [isValid, _, errors] = validateSetPasswordFormValue({
      password: password,
      confirmPassword: confirmPassword ?? "",
    })

    if (isValid) {
      setFormErrors({
        password: [],
        confirmPassword: [],
      })
    } else {
      setFormErrors({
        password: errors.password?._errors ?? [],
        confirmPassword: errors.confirmPassword?._errors ?? [],
      })
    }

    return isValid
  }

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.currentTarget.reportValidity()

    if (!validateForm(resetPasswordInputs)) {
      return
    }

    setSubmitting(true)
    dispatch(setAuthFlowLoading(true))

    try {
      const response = await userApi.resetPassword(
        resetPasswordToken,
        resetPasswordInputs.password
      )

      if (response.user.email) {
        try {
          await dispatch(
            credentialSignIn({
              email: response.user.email,
              password: resetPasswordInputs.password,
              cf_token: cfToken,
              callbackUrl,
            })
          ).unwrap()

          sendAnalyticsEvent("ResetPasswordSubmit")
        } catch (e) {
          const errorMessage = "Unable to sign in"
          dispatch(setAuthFlowError(errorMessage))
          sendAnalyticsEvent({
            name: "ResetPasswordFail",
            properties: errorToAmplitudeProperties(errorMessage),
          })
        }
      } else {
        const errorMessage =
          "This password reset link is invalid or has expired. Please request a new one."
        dispatch(setAuthFlowError(errorMessage))
        sendAnalyticsEvent({
          name: "ResetPasswordFail",
          properties: errorToAmplitudeProperties(errorMessage),
        })
      }
    } catch (error) {
      console.error("Error resetting password:", error)
      const errorMsg = getResetPasswordErrorMessage(error)

      sendAnalyticsEvent({
        name: "ResetPasswordFail",
        properties: errorToAmplitudeProperties(errorMsg),
      })
      dispatch(setAuthFlowError(errorMsg))
    } finally {
      setSubmitting(false)
      dispatch(setAuthFlowLoading(false))
    }
  }

  function passwordChanged(password: string) {
    setResetPasswordInputs((prev) => ({ ...prev, password }))

    validateForm({
      password,
      confirmPassword: resetPasswordInputs.confirmPassword,
    })
  }

  function confirmPasswordChanged(confirmPassword: string) {
    setResetPasswordInputs((prev) => ({ ...prev, confirmPassword }))

    validateForm({
      password: resetPasswordInputs.password,
      confirmPassword,
    })
  }

  return (
    <Form method="post" onSubmit={handleSubmit}>
      <FormField name="password" invalid={!!formErrors.password.length}>
        <FormControl
          render={
            <TextInput
              type="password"
              placeholder="New password"
              autoComplete="new-password"
              required
              size="lg"
              onChange={(e) => passwordChanged(e.target.value)}
              disabled={submitting}
            />
          }
        />
        {!!resetPasswordInputs.password.length &&
          formErrors.password.map((error, i) => (
            <FormError key={`password_errors_${i}`}>{error}</FormError>
          ))}
      </FormField>
      <FormField name="password2" invalid={!!formErrors.confirmPassword.length}>
        <FormControl
          render={
            <TextInput
              type="password"
              placeholder="Confirm new password"
              autoComplete="new-password"
              required
              size="lg"
              onChange={(e) => confirmPasswordChanged(e.target.value)}
              disabled={submitting}
            />
          }
        />
        {!!resetPasswordInputs.confirmPassword?.length &&
          formErrors.confirmPassword.map((error, i) => (
            <FormError key={`confirm_password_errors_${i}`}>{error}</FormError>
          ))}
      </FormField>
      {shouldTurnstile("sign-in") ? (
        <div className="m-2 flex justify-center">
          <Turnstile
            id="reset-password-turnstile"
            onSuccess={setCfToken}
            onError={handleTurnstileError}
            onExpire={handleTurnstileRetry}
            onUnsupported={handleTurnstileFailure}
            onScriptLoadError={handleTurnstileFailure}
          />
        </div>
      ) : null}
      {error && <p className="font-medium text-destructive">{error}</p>}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        block
        loading={submitting}
      >
        Continue
      </Button>
    </Form>
  )
}
